import React, { useEffect, useState } from "react";
import { api } from "@/planner/lib/apiClient";
import { productLabel } from "@/planner/lib/format";
import { unitCost, money } from "@/planner/lib/pricing";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";

export const WASTE_REASONS = [["damaged", "Damaged"], ["expired", "Expired"], ["missing", "Missing"], ["other", "Other"]];
export const wasteReason = (k) => (WASTE_REASONS.find(([v]) => v === k) || [k, k])[1];

// Log product that was thrown out: it comes out of backstock and its cost is recorded (POST /waste).
export const WasteForm = ({ product, onSaved, onCancel }) => {
  const [units, setUnits] = useState("1");
  const [reason, setReason] = useState("damaged");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setUnits("1"); setReason("damaged"); setNote(""); }, [product?.id]);
  const n = Math.round(Number(units)) || 0;
  const uc = unitCost(product);

  const save = async (e) => {
    e.preventDefault();
    if (n < 1) { toast.error("Enter how many were thrown out."); return; }
    setSaving(true);
    try {
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const { data } = await api.post("/waste", { product_id: product.id, units: n, reason, note, date: today });
      toast.success(`${productLabel(product)}: ${n} logged as waste${data.cost != null ? ` (${money(data.cost)})` : ""}`);
      onSaved?.(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't log the waste."); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="space-y-3" data-testid="waste-form">
      <div>
        <Label htmlFor="waste-units">How many?</Label>
        <div className="mt-1 flex items-center gap-2">
          <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setUnits(String(Math.max(1, n - 1)))} aria-label="One less"><Minus className="h-4 w-4" /></Button>
          <Input id="waste-units" type="number" inputMode="numeric" min="1" value={units} onChange={(e) => setUnits(e.target.value)} onFocus={(e) => e.target.select()} className="h-10 text-center text-lg font-bold" data-testid="waste-units" />
          <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setUnits(String(n + 1))} aria-label="One more"><Plus className="h-4 w-4" /></Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {product.on_hand ?? 0} in backstock{uc != null && n > 0 ? ` · loss ${money(uc * n)} at ${money(uc)} each` : uc == null ? " · no cost on this product yet" : ""}
        </p>
      </div>
      <div>
        <Label>Why?</Label>
        <div className="mt-1 grid grid-cols-4 gap-1 rounded-lg bg-muted p-1 text-sm" role="radiogroup" aria-label="Why">
          {WASTE_REASONS.map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={reason === k} onClick={() => setReason(k)}
              className={`rounded-md px-2 py-1.5 ${reason === k ? "bg-background font-semibold shadow" : "text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <Label htmlFor="waste-note">Note (optional)</Label>
        <Input id="waste-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="e.g. Bag broke open while unpacking" className="mt-1" />
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="destructive" className="flex-1" disabled={saving} data-testid="waste-save">{saving ? "Saving…" : `Log ${n > 0 ? n : ""} as waste`}</Button>
      </div>
    </form>
  );
};

export default function WasteDialog({ product, onClose, onSaved }) {
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="waste-dialog">
        <DialogHeader>
          <DialogTitle>Log waste</DialogTitle>
          <DialogDescription>{product ? `${productLabel(product)}${product.size ? ` · ${product.size}` : ""}` : ""}. It comes out of backstock and the loss is recorded.</DialogDescription>
        </DialogHeader>
        {product && <WasteForm product={product} onCancel={onClose} onSaved={(d) => { onSaved?.(d); onClose(); }} />}
      </DialogContent>
    </Dialog>
  );
}
